import { MutableRefObject, RefObject, useEffect, useRef, useState } from 'react'
import { Point, ResultFrame, TwoDPoint } from '@entities/Frame'
import { CircleInfo } from '@features/exercise/AIVideo/process/types'

export const useVideoFromFrames = (
	canvasRef: RefObject<HTMLCanvasElement>,
	videoRef: RefObject<HTMLVideoElement>, // Добавляем ref на видео
	frames: { frame: string; frameData: ResultFrame }[],
	delayTime: number,
	increaseReplayCounter: () => void
) => {
	const [isPlaying, setIsPlaying] = useState<boolean>(true)
	const [currentFrame, setCurrentFrame] = useState<number>(0)
	const [circlePositions, setCirclePositions] = useState<CircleInfo[]>([])

	const frameCount: number = frames.length
	const countRef: MutableRefObject<number> = useRef<number>(0)

	const canvasWidthRef: MutableRefObject<number | null> = useRef<number | null>(null)
	const canvasHeightRef: MutableRefObject<number | null> = useRef<number | null>(null)

	const calculateWidth = (scale: number) => {
		if (canvasWidthRef.current === null) {
			canvasWidthRef.current = canvasRef.current?.width ?? 0
		}
		return canvasWidthRef.current * scale
	}

	const calculateHeight = (scale: number) => {
		if (canvasHeightRef.current === null) {
			canvasHeightRef.current = canvasRef.current?.height ?? 0
		}
		return canvasHeightRef.current * scale
	}

	const calculatePlacementInfo = (point: TwoDPoint, isReference: boolean): CircleInfo => ({
		x: calculateWidth(point.x),
		y: calculateHeight(point.y),
		viewType: isReference ? 'ref' : 'common',
	})

	const drawLine = (point1: Point, point2: Point, context: CanvasRenderingContext2D) => {
		const x1: number = calculateWidth(point1.x)
		const x2: number = calculateWidth(point2.x)
		const y1: number = calculateHeight(point1.y)
		const y2: number = calculateHeight(point2.y)

		context.beginPath()
		context.lineWidth = 2
		context.strokeStyle = '#a8e59e'

		context.moveTo(x1, y1)
		context.lineTo(x2, y2)
		context.closePath()

		context.stroke()
	}

	const drawPoints = (context: CanvasRenderingContext2D) => {
		const circleCoords: CircleInfo[] = []

		frames[countRef.current].frameData.metrics.angles.forEach(angle => {
			drawLine(angle.ref_point, angle.point1, context)
			drawLine(angle.ref_point, angle.point2, context)

			const addPoint = (point: Point, isReference: boolean) => {
				const epsilon: number = 0.0001 // Погрешность для сравнения
				const transformedPoint = calculatePlacementInfo(point, isReference)

				const existingPoint = circleCoords.find(
					s =>
						Math.abs(s.x - transformedPoint.x) < epsilon &&
						Math.abs(s.y - transformedPoint.y) < epsilon
				)

				if (!existingPoint) {
					circleCoords.push(transformedPoint)
				} else if (isReference) {
					existingPoint.viewType = 'ref'
				}
			}

			addPoint(angle.ref_point, true)
			addPoint(angle.point1, false)
			addPoint(angle.point2, false)
		})

		setCirclePositions(circleCoords)
	}

	useEffect(() => {
		const isCheckExercise: boolean = window.location.href.includes('check_exercise')

		if (!isCheckExercise) {
			setIsPlaying(false)
			setCurrentFrame(0)
			setCirclePositions([])
			countRef.current = 0

			if (canvasRef.current) {
				const context = canvasRef.current.getContext('2d')
				if (context) {
					context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height)
				}
			}

			return
		}

		if (!canvasRef.current || !videoRef.current || !isPlaying) return

		const canvas: HTMLCanvasElement = canvasRef.current
		const context: CanvasRenderingContext2D | null = canvas.getContext('2d')
		const video: HTMLVideoElement = videoRef.current

		if (!context) return

		const updateFrame = () => {
			const currentTime = video.currentTime
			const frameIndex = Math.floor((currentTime / video.duration) * frameCount)

			if (frameIndex !== countRef.current) {
				countRef.current = frameIndex
				setCurrentFrame(frameIndex)

				context.clearRect(0, 0, canvas.width, canvas.height)
				drawPoints(context)

				if (frameIndex === 0) {
					increaseReplayCounter()
				}
			}

			if (isPlaying) {
				requestAnimationFrame(updateFrame)
			}
		}

		video.addEventListener('timeupdate', updateFrame)

		return () => {
			video.removeEventListener('timeupdate', updateFrame)
		}
	}, [canvasRef, videoRef, isPlaying, frames, frameCount])

	const togglePlayPause = () => {
		setIsPlaying(prev => !prev)
		if (videoRef.current) {
			if (isPlaying) {
				videoRef.current.pause()
			} else {
				videoRef.current.play()
			}
		}
	}

	return { isVideoPlaying: isPlaying, togglePlayPause, currentFrame, circlePositions }
}
